import { Formik } from 'formik';
import { useState, FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineArrowLeft, HiOutlineMap } from 'react-icons/hi';
import { tourSchema } from '../../../../../constants';
import TourForm from '../sections/TourForm';
import { DAV_APIS } from '../../../../../adapters';
import { Alert, CustomEditor, ImageUploader, SEO } from '../../../../commons';
import SwitchInput from '../../../../commons/SwitchInput';

import { Card } from '../../../../ui/Card';
import { Button } from '../../../../ui/Button';

const NewTourPage: FC = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const newTourSubmitHandler = async (values: any) => {
    setLoading(true);
    try {
      const tourActivities: string[] = [];
      values.tourActivities.split('\n').map((item: string) => tourActivities.push(item));

      const priceIncludes: string[] = [];
      values.priceIncludes.split('\n').map((item: string) => priceIncludes.push(item));

      const priceExcludes: string[] = [];
      values.priceExcludes.split('\n').map((item: string) => priceExcludes.push(item));

      const packageDetails = {
        price_inludes: priceIncludes,
        price_excludes: priceExcludes,
      };

      if (values.map instanceof File) {
        const mapUrl = await DAV_APIS.tour.uploadImage(values.map);
        values.map = mapUrl?.data?.url;
      }

      const data = {
        name: values.title,
        description: values.description,
        tourActivities: JSON.stringify(tourActivities),
        file: values.imageFile,
        country: values.country.toLowerCase(),
        category: values.category,
        duration: values.days,
        price: values.price,
        dayActivityDescription: JSON.stringify(values.itineraries),
        packageDetails: JSON.stringify(packageDetails),
        journey: values.journey,
        isFeatured: values.isFeatured,
        map: values.map,
        schedules: values.enableScheduledTours ? values.schedules : [],
      };
      const res = await DAV_APIS.tour.createNewTour(data);
      setLoading(false);
      if (res.status === 201) {
        Alert({ variant: 'success', message: 'Tour package created successfully' });
        navigate('/dashboard/tours');
      }
    } catch (error) {
      setLoading(false);
      Alert({ variant: 'error', message: 'Failed to create package, Please try again' });
    }
  };

  return (
    <div className='w-full max-w-7xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700'>
      {/* Header */}
      <div className='flex items-center gap-4'>
        <Button
          variant='ghost'
          onClick={() => navigate('/dashboard/tours')}
          leftIcon={<HiOutlineArrowLeft className='w-5 h-5' />}
        >
          Back to Tours
        </Button>
      </div>

      <Formik
        enableReinitialize
        initialValues={{
          title: '',
          description: '',
          imageCover: '',
          imageFile: '',
          tourActivities: '',
          country: '',
          category: '',
          days: '',
          price: '',
          priceIncludes: '',
          priceExcludes: '',
          itineraries: [],
          day: '',
          itinaryTitle: '',
          itinaryDescription: '',
          meal_plan: '',
          accommodation_plan: '',
          journey: '',
          isFeatured: true,
          map: '',
          schedules: [],
          enableScheduledTours: false,
        }}
        validationSchema={tourSchema}
        onSubmit={async (values, { resetForm }) => {
          await newTourSubmitHandler(values);

          resetForm();
        }}
      >
        {({ handleSubmit, values, setFieldValue, dirty, isValid }) => (
          <>
            <form onSubmit={handleSubmit}>
              <Card variant='glass' className='p-6 md:p-8 border-none shadow-xl shadow-gray-200/50 dark:shadow-none'>
                <div className='mb-8 border-b border-gray-100 dark:border-gray-700/50 pb-6'>
                  <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>Create New Tour Package</h2>
                  <p className='text-gray-500 dark:text-gray-400 mt-1'>Fill in the details below to add a new safari experience.</p>
                </div>

                <div className='space-y-8'>
                  <TourForm values={values} setFieldValue={setFieldValue} />
                  
                  <div className='pt-4 border-t border-gray-100 dark:border-gray-700/50'>
                    <section>
                      <div className='flex items-center gap-2 mb-6 text-primary-600 dark:text-primary-400'>
                        <HiOutlineMap className='w-5 h-5' />
                        <h3 className='text-lg font-semibold text-gray-800 dark:text-gray-100'>
                          Additional Options & Journey Map 
                          <span className='ml-2 text-sm font-normal text-gray-500 dark:text-gray-400'>(Optional)</span>
                        </h3>
                      </div>
                      
                      <div className='space-y-8 px-2'>
                        <div className='space-y-4'>
                          <div className='flex items-center gap-2 text-primary-600 dark:text-primary-400'>
                              <h4 className='text-sm uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400'>Journey Details</h4>
                          </div>
                          <CustomEditor
                            placeholder='Type a summarized tour journey from start to finish here...'
                            onChange={(val: any) => setFieldValue('journey', val)}
                            value={values.journey}
                            name='journey'
                          />
                        </div>
                        
                        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                          <div className='p-5 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-700/50 hover:border-primary-200 transition-colors'>
                            <h5 className='text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2'>
                              <span className='w-1.5 h-1.5 rounded-full bg-primary-500' />
                              Journey Map
                            </h5>
                            <ImageUploader label='Upload/Change Map' onUpload={(file) => setFieldValue('map', file)} />
                          </div>
                          <div className='p-5 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-700/50 hover:border-primary-200 transition-colors flex flex-col justify-center'>
                              <h5 className='text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2'>
                              <span className='w-1.5 h-1.5 rounded-full bg-yellow-400' />
                              Featured Status
                            </h5>
                            <div className='flex items-center justify-between bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700'>
                              <span className='text-sm text-gray-600 dark:text-gray-300'>Show on Landing Page</span>
                              <SwitchInput
                                checked={values.isFeatured}
                                onChange={(event) => setFieldValue('isFeatured', event)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>

                  <div className='flex justify-end gap-3 pt-6 border-t border-gray-100 dark:border-gray-700/50'>
                    <Button
                      variant='outline-secondary'
                      onClick={() => navigate('/dashboard/tours')}
                    >
                      Cancel
                    </Button>
                    <Button
                      type='submit'
                      variant='primary'
                      loading={loading}
                      disabled={loading || !dirty || !isValid}
                      className='min-w-[140px]'
                    >
                      {loading ? 'Creating...' : 'Create Tour'}
                    </Button>
                  </div>
                </div>
              </Card>
            </form>

            <SEO
              pageType='tour'
              initialData={{
                title: values.title,
                description: values.description,
                image: values.imageCover,
              }}
            />
          </>
        )}
      </Formik>
    </div>
  );
};

export default NewTourPage;
